import { Anchor, Button, Group, Modal, Stack, Text } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { CrudSection } from '../../components/CrudSection';
import { EntityLabel } from '../../components/EntityField';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { formatDateTime, formatMoney } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { OrderDetailModal } from './OrderDetail';
import { ORDERS_PATH } from './orders';
import { PlaceOrderForm } from './PlaceOrderForm';

interface OrderRow {
  id: string;
  supplierId: string;
  status: string;
  orderedAt?: string | null;
  note?: string | null;
  lineCount: number;
  totalMinor: number;
  currency?: string | null;
}

/**
 * Who an order went to, as the way into the order.
 *
 * The name is the link rather than an Open button on every row: a manager scans this table to find
 * one order, and a column of identical buttons is noise down the page. It is the same shape the
 * supplier list uses to reach a vendor.
 */
function OrderLink({ supplierId, onOpen }: { supplierId: string; onOpen: () => void }) {
  const suppliers = useEntityIndex('supplier');
  return (
    <Anchor component="button" type="button" fw={600} onClick={onOpen} ta="left">
      {suppliers.label(supplierId)}
    </Anchor>
  );
}

/**
 * What has been asked for and not yet received.
 *
 * The list used to be the whole tab, and nothing anywhere could add to it — so it was permanently
 * empty, and so was the "on order" column beside every supplier. An order matters for two reasons
 * only: it says what is coming, and it gives the delivery something to be matched against when it
 * lands. Both need it to be writable, and both need it to be READABLE: what was on an order lived
 * nowhere at all once the form that created it closed.
 */
export function OrdersSection({ locationId }: { locationId?: string }) {
  const queryClient = useQueryClient();
  const [placing, setPlacing] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<OrderRow | null>(null);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: listKey(ORDERS_PATH) });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'sent' | 'cancelled' }) =>
      rawApi('POST', `${ORDERS_PATH}/${id}/status`, { status }),
    onSuccess: (_data, { status }) => {
      refresh();
      setCancelling(null);
      notifyDone(
        status === 'sent' ? 'Marked as sent' : 'Order cancelled',
        status === 'sent'
          ? 'It now counts as on order, and a delivery can be recorded against it.'
          : 'It is no longer counted as on order. Nothing else has changed.',
      );
    },
    onError: (err: Error) => notifyError(err, 'change this order'),
  });

  return (
    <>
      <CrudSection
        title="Orders"
        description="What you have asked your suppliers for. Record an order when you send it, and when the goods turn up the delivery can be recorded against it — which is what closes the order and opens their bill."
        listPath={ORDERS_PATH}
        emptyMessage="No orders on file. Record one when you send an order to a supplier."
        actions={
          <Button size="sm" onClick={() => setPlacing(true)}>
            Record an order
          </Button>
        }
        // No expected date and no order number: nothing in this system sets either, and a column
        // that is empty on every row for ever is a column that only teaches people to look past it.
        // An order is known by who it went to, when, and what it was for.
        columns={[
          {
            accessor: 'supplierId',
            title: 'Supplier',
            render: (r) => (
              <OrderLink supplierId={String(r.supplierId)} onOpen={() => setOpening(String(r.id))} />
            ),
          },
          { accessor: 'status', title: 'Status', enumGroup: 'purchaseOrderStatus' },
          {
            accessor: 'orderedAt',
            title: 'Sent',
            noWrap: true,
            // A draft has no sent date, and a dash there reads as a date nobody filled in rather
            // than as an order that has not gone out — which is the row that needs acting on.
            render: (r) =>
              r.orderedAt ? (
                formatDateTime(String(r.orderedAt))
              ) : (
                <Text size="sm" c="dimmed">
                  Not sent yet
                </Text>
              ),
          },
          { accessor: 'note', title: 'What it is for' },
          {
            // The one figure a list of orders is read for, and it was not on it: every row had to
            // be opened to find out whether the order was two crates or two thousand lempiras.
            accessor: 'totalMinor',
            title: 'Comes to',
            noWrap: true,
            render: (r) => {
              const order = r as unknown as OrderRow;
              if (!order.lineCount || !order.currency) {
                return (
                  <Text size="sm" c="dimmed">
                    Nothing on it yet
                  </Text>
                );
              }
              return (
                <>
                  <Text size="sm" fw={600}>
                    {formatMoney(order.totalMinor, order.currency)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {order.lineCount === 1 ? '1 item' : `${order.lineCount} items`}
                  </Text>
                </>
              );
            },
          },
        ]}
        rowActions={(row) => {
          const order = row as unknown as OrderRow;
          if (order.status === 'draft') {
            return (
              <Button
                size="xs"
                variant="light"
                loading={setStatus.isPending}
                onClick={() => setStatus.mutate({ id: order.id, status: 'sent' })}
              >
                Mark as sent
              </Button>
            );
          }
          if (order.status === 'sent' || order.status === 'partiallyReceived') {
            return (
              <Button size="xs" variant="subtle" color="red" onClick={() => setCancelling(order)}>
                Cancel it
              </Button>
            );
          }
          return null;
        }}
      />

      {placing && (
        <PlaceOrderForm locationId={locationId} onClose={() => setPlacing(false)} onSaved={refresh} />
      )}

      {/* What the order was for, and where the delivery of it is recorded — on the order, rather
          than on a tab that then asks who delivered it and which order it was against. */}
      {opening && (
        <OrderDetailModal
          orderId={opening}
          locationId={locationId}
          onClose={() => setOpening(null)}
          onChanged={refresh}
        />
      )}

      {/* Asked rather than done on the click: cancelling is the one thing here that cannot be put
          back, and it sits a few pixels from the buttons that only read. */}
      <Modal
        opened={cancelling !== null}
        onClose={() => setCancelling(null)}
        title="Cancel this order?"
        size="md"
      >
        {cancelling && (
          <Stack gap="md">
            <Group gap={6} wrap="wrap">
              <Text size="sm">The order to</Text>
              <EntityLabel entity="supplier" id={cancelling.supplierId} />
              <Text size="sm">
                {cancelling.note ? `for ${cancelling.note}` : ''} will stop counting as on order.
              </Text>
            </Group>
            <Text size="sm" c="dimmed">
              Nothing that has already arrived is affected. If part of it turned up, record that delivery
              first.
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setCancelling(null)} disabled={setStatus.isPending}>
                Keep it
              </Button>
              <Button
                color="red"
                loading={setStatus.isPending}
                onClick={() => setStatus.mutate({ id: cancelling.id, status: 'cancelled' })}
              >
                Cancel the order
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
